import React from 'react';
import { useTranslation } from 'react-i18next';
import { MessageBarActions, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideBannerAction } from '../../../theme/components/GuideButtons';
import { GuideHelpPopover } from '../../../theme/components/GuideHelpPopover';
import { GuideSkeleton } from '../../../theme/components/GuideSkeleton';
import { ErrorType } from '../../../models/HttpError';

/** Traducción del estado de cuenta: todas las claves cuelgan de `practitionerAccountStatement`. */
export const useStatementText = () => {
    const [t] = useTranslation();
    return (key: string, options?: Record<string, unknown>): string => t(`practitionerAccountStatement.${key}`, options as any) as string;
};

/** Ayuda (?) de la guía: popover con título y explicación, al hacer click. */
export const HelpButton = ({ title, text }: { title: string; text: string }) => {
    const tr = useStatementText();
    return <GuideHelpPopover title={title} text={text} ariaLabel={tr('help.ariaLabel', { title })} />;
};

/** Carga: skeleton con barras grises y "Cargando…". */
export const BlockSkeleton = ({ rows = 3, testId }: { rows?: number; testId?: string }) => {
    const tr = useStatementText();
    return <GuideSkeleton rows={rows} label={tr('states.loading')} data-testid={testId} />;
};

/** Error de un bloque, con "Reintentar" (acción subrayada del banner). */
export const ErrorBanner = ({ title, error, onRetry }: { title: string; error?: ErrorType; onRetry: () => void }) => {
    const tr = useStatementText();
    return (
        <GuideMessageBar intent="error" layout="multiline">
            <MessageBarBody>
                <MessageBarTitle>{title}</MessageBarTitle>
                {error?.ErrorMessage || tr('states.tryAgain')}
            </MessageBarBody>
            <MessageBarActions>
                <GuideBannerAction onClick={onRetry}>{tr('actions.retry')}</GuideBannerAction>
            </MessageBarActions>
        </GuideMessageBar>
    );
};

/** Endpoint todavía no publicado: "dato no disponible todavía". */
export const UnavailableBanner = ({ what }: { what: string }) => {
    const tr = useStatementText();
    return (
        <GuideMessageBar intent="info" layout="multiline">
            <MessageBarBody>
                <MessageBarTitle>{tr('states.unavailableTitle')}</MessageBarTitle>
                {tr('states.unavailableText', { what })}
            </MessageBarBody>
        </GuideMessageBar>
    );
};
